import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect, useCallback, useMemo } from "react";

// LetterAnimator — animates a "pencil" travelling along each reference stroke
// sequentially. Between strokes it lifts the pen, jumps to the next stroke's
// starting point (drawn as a pulsing dot) and pauses briefly so kids can see
// where the next stroke begins. Exposes play / pause / replay / reset.
const PER_STROKE_MS = 2200;   // duration to draw one stroke
const PEN_LIFT_MS = 650;      // pause between strokes

export const LetterAnimator = forwardRef(({ letter, onStateChange }, ref) => {
  const strokes = useMemo(() => (letter && letter.strokes) || [], [letter]);
  const pathRefs = useRef([]);
  const totals = useRef([]);
  const rafRef = useRef(null);
  const timerRef = useRef(null);

  const [strokeIdx, setStrokeIdx] = useState(0);
  const [progress, setProgress] = useState(0);      // 0..1 within the current stroke
  const [completed, setCompleted] = useState(0);    // number of fully drawn strokes
  const [tip, setTip] = useState(null);
  const [showNextDot, setShowNextDot] = useState(false); // pulsing start dot between strokes
  const [playing, setPlaying] = useState(false);

  const startTsRef = useRef(0);
  const startProgRef = useRef(0);

  useEffect(() => {
    totals.current = pathRefs.current.map((el) => (el ? el.getTotalLength() : 0));
    setStrokeIdx(0);
    setProgress(0);
    setCompleted(0);
    setTip(null);
    setShowNextDot(false);
    setPlaying(false);
  }, [letter]);

  const clearTimers = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (timerRef.current) clearTimeout(timerRef.current);
    rafRef.current = null;
    timerRef.current = null;
  }, []);

  const updateTip = useCallback((i, p) => {
    const el = pathRefs.current[i];
    if (!el) return;
    const total = totals.current[i] || el.getTotalLength() || 1;
    const pt = el.getPointAtLength(p * total);
    setTip({ x: pt.x, y: pt.y });
  }, []);

  const stop = useCallback(() => {
    clearTimers();
    setPlaying(false);
    onStateChange && onStateChange(false);
  }, [clearTimers, onStateChange]);

  // Forward-declared so tick can call startStroke on completion.
  const startStrokeRef = useRef(null);

  const tick = useCallback(
    (i) => (ts) => {
      if (!startTsRef.current) startTsRef.current = ts;
      const elapsed = ts - startTsRef.current;
      const p = Math.min(1, startProgRef.current + elapsed / PER_STROKE_MS);
      setProgress(p);
      updateTip(i, p);
      if (p < 1) {
        rafRef.current = requestAnimationFrame(tick(i));
      } else {
        setCompleted(i + 1);
        // If there's a next stroke, lift the pen and pulse its start dot.
        if (i + 1 < strokes.length) {
          setShowNextDot(true);
          setTip(null);
          timerRef.current = setTimeout(() => {
            setShowNextDot(false);
            startStrokeRef.current(i + 1, 0);
          }, PEN_LIFT_MS);
        } else {
          setPlaying(false);
          onStateChange && onStateChange(false);
        }
      }
    },
    [strokes.length, updateTip, onStateChange]
  );

  const startStroke = useCallback(
    (i, from) => {
      clearTimers();
      startTsRef.current = 0;
      startProgRef.current = from;
      setStrokeIdx(i);
      setProgress(from);
      updateTip(i, from);
      setPlaying(true);
      onStateChange && onStateChange(true);
      rafRef.current = requestAnimationFrame(tick(i));
    },
    [clearTimers, tick, updateTip, onStateChange]
  );

  useEffect(() => { startStrokeRef.current = startStroke; }, [startStroke]);

  const resumeOrStart = useCallback(() => {
    // Resume from where we paused; if we already finished the last stroke, start over.
    const done = completed >= strokes.length && strokes.length > 0;
    if (done) { startStroke(0, 0); return; }
    startStroke(strokeIdx, progress >= 1 ? 0 : progress);
  }, [startStroke, completed, strokes.length, strokeIdx, progress]);

  useImperativeHandle(ref, () => ({
    play: () => resumeOrStart(),
    pause: () => stop(),
    replay: () => startStroke(0, 0),
    reset: () => {
      stop();
      setStrokeIdx(0);
      setProgress(0);
      setCompleted(0);
      setTip(null);
      setShowNextDot(false);
    },
    isPlaying: () => playing,
  }));

  useEffect(() => () => clearTimers(), [clearTimers]);

  return (
    <g>
      {/* Hidden reference paths used for length + point lookup. */}
      {strokes.map((s, i) => (
        <path
          key={`ref-${i}`}
          ref={(el) => { pathRefs.current[i] = el; }}
          d={s.d}
          fill="none"
          stroke="none"
        />
      ))}
      {/* Progressively revealed strokes (completed strokes stay fully drawn). */}
      {strokes.map((s, i) => {
        if (i > strokeIdx) return null; // future strokes stay hidden
        const total = totals.current[i] || 1;
        const p = i < strokeIdx ? 1 : progress; // completed strokes stay full
        return (
          <path
            key={`draw-${i}`}
            d={s.d}
            fill="none"
            stroke="#F59E0B"
            strokeWidth="4.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              strokeDasharray: total,
              strokeDashoffset: total * (1 - p),
              transition: "none",
            }}
            opacity={p > 0 ? 0.95 : 0}
          />
        );
      })}
      {/* Pulsing "start here" dot for the next stroke (between strokes). */}
      {showNextDot && strokes[strokeIdx + 1] && strokes[strokeIdx + 1].start && (
        <g>
          <circle
            cx={strokes[strokeIdx + 1].start.x}
            cy={strokes[strokeIdx + 1].start.y}
            r="3.8"
            fill="#F59E0B"
            opacity="0.9"
          >
            <animate attributeName="r" values="3.4;5.2;3.4" dur="0.9s" repeatCount="indefinite" />
          </circle>
          <circle
            cx={strokes[strokeIdx + 1].start.x}
            cy={strokes[strokeIdx + 1].start.y}
            r="1.5"
            fill="#fff"
          />
        </g>
      )}
      {/* Live pen tip on the stroke currently being drawn. */}
      {tip && !showNextDot && (
        <g>
          <circle cx={tip.x} cy={tip.y} r="3.2" fill="#D97706" />
          <circle cx={tip.x} cy={tip.y} r="1.3" fill="#fff" />
        </g>
      )}
    </g>
  );
});

LetterAnimator.displayName = "LetterAnimator";
